import React from "react";

type PanelProps = {
  className?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
};

/** A paper panel with hairline border and "+" registration marks. */
export function Panel({ className, children, style }: PanelProps) {
  return (
    <section className={`panel ${className ?? ""}`} style={style}>
      {children}
    </section>
  );
}

type PanelGroupProps = {
  className?: string;
  children: React.ReactNode;
  /** Lay sections out side by side instead of stacked. */
  row?: boolean;
};

/** One seamless card holding several panels, with hatched dividers between. */
export function PanelGroup({ className, children, row }: PanelGroupProps) {
  const sections = React.Children.toArray(children).filter(Boolean);
  return (
    <div
      className={`panel-group ${row ? "panel-group--row" : ""} ${className ?? ""}`}
    >
      {sections.map((section, i) => (
        <React.Fragment key={i}>
          {i > 0 && <div className="panel-divider" aria-hidden />}
          {section}
        </React.Fragment>
      ))}
    </div>
  );
}

type PanelHeaderProps = {
  label: React.ReactNode;
  meta?: React.ReactNode;
  accent?: boolean;
};

/** Mono uppercase label row at the top of a panel. */
export function PanelHeader({ label, meta, accent }: PanelHeaderProps) {
  return (
    <header className="panel-head">
      <span className="panel-head__label">{label}</span>
      {meta !== undefined && meta !== null && (
        <span className={`panel-head__meta ${accent ? "is-accent" : ""}`}>
          {meta}
        </span>
      )}
    </header>
  );
}

/** Centered message for loading, empty and error states. */
export function PanelState({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone?: "error";
}) {
  return (
    <div className={`panel-state ${tone === "error" ? "is-error" : ""}`}>
      {children}
    </div>
  );
}
